/**
 * EngineerMultiSelect.jsx - picks engineers for a project.
 *
 * The engineer column has always been free text ("Ian", "team / RR", "?"),
 * which is why engineer-to-project matching has to guess by first name. This
 * control keeps writing the same comma-separated string, but only ever writes
 * canonical names from the registry (plus deliberate custom entries), so new
 * rows stop adding to the mess.
 *
 * Used by the Projects form and (r49) the opportunity editor, which passes
 * `selectStyle` so the picker matches its own inputs. `onRemove` (the red x
 * on a list item, RefPicker) is passed only to people who may change the list.
 */
import { INPUT } from './formStyles';
import RefPicker from './RefPicker';

function EngineerMultiSelect({ value, onChange, options, addRef, onRemove, selectStyle = INPUT }) {
  const selected = String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const available = options.filter((n) => !selected.includes(n));
  const commit = (list) => onChange(list.join(', '));
  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 6 }}>
          {selected.map((name) => (
            <span
              key={name}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#E8F5E9',
                border: '1px solid #C8E6C9',
                color: '#1B5E20',
                borderRadius: 12,
                padding: '2px 6px 2px 10px',
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: 'Montserrat',
              }}
            >
              {name}
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={() => commit(selected.filter((n) => n !== name))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#557052',
                  cursor: 'pointer',
                  fontSize: 12,
                  lineHeight: 1,
                  padding: '0 2px',
                  fontFamily: 'Montserrat',
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <RefPicker
        label="Add engineer"
        style={selectStyle}
        value=""
        placeholder={selected.length ? '+ Add another…' : '+ Add engineer…'}
        clearable={false}
        options={available}
        onChange={(v) => {
          if (v) commit([...selected, v]);
        }}
        onAddNew={async () => {
          const entered = window.prompt('Engineer / vendor name:');
          const clean = entered ? await addRef('engineer', entered) : null;
          if (clean && !selected.includes(clean)) commit([...selected, clean]);
        }}
        onRemove={onRemove}
      />
    </div>
  );
}

export default EngineerMultiSelect;
